import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

import { PATH_DOCUMENT_TEMPLATE_PRINTER } from '@e-socna/printer-contract';

import {
  PATH_DOCUMENT_AND_SECTION_TEMPLATES_DEFAULT,
  PATH_DOCUMENT_TEMPLATES,
  PATH_DOCUMENT_TEMPLATE_ADMIN,
  PATH_DOCUMENT_TEMPLATE_ADMIN_EDIT,
  PATH_DOCUMENT_TEMPLATE_DEFAULT,
  PATH_DOCUMENT_TEMPLATE_EDITOR,
  PATH_DOCUMENT_TEMPLATE_INFO,
  PATH_DOCUMENT_TEMPLATE_INFO_EDIT,
  PATH_DOCUMENT_TEMPLATE_PREVIEW,
  PATH_DOCUMENT_TEMPLATE_GENERATION,
  PATH_DOCUMENT_TEMPLATE_VERSIONS,
  PATH_DOCUMENT_TEMPLATE_SUMMARY,
} from '../../app/constants/paths.constant';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { DocumentEditor } from '../../components/DocumentEditor';
import { Loading } from '../../components/Loading';
import { FormVariant } from '../../forms';
import { BackupMode } from '../../components/DocumentEditor/enums/backup-mode.enum';
import { useIdParam } from '../../utils/use-id-param.util';
import { Page } from '../../contexts/NavigationContext/enums/page.enum';
import { useNavigationContext } from '../../contexts/NavigationContext/hooks/useNavigationContext.hook';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';
import { useBuildPath } from '../../hooks/useBuildPath.hook';
import { buildDocumentTitle } from '../../utils/document-title.util';
import { PageTitle } from '../../components/PageTitle';

import { useGetters } from './hooks/useGetters.hook';
import { DocumentTemplateInfoTabPanel } from './DocumentTemplateInfo';
import { useFindOneDocumentTemplateQuery } from './hooks/useFindOneDocumentTemplateQuery.hook';
import { useSaveHandler } from './hooks/useSaveHandler.hook';
import { useSavePreviewHandler } from './hooks/useSavePreviewHandler.hook';
import { usePrintHandler } from './hooks/usePrintHandler.hook';
import { useSavePrintSettingsHandler } from './hooks/useSavePrintSettingsHandler.hook';
import type { DocumentTemplatePage_OneDocumentTemplate } from './hooks/useFindOneDocumentTemplateQuery.hook';

interface Props {
  readonly isInPrintMode?: boolean;
}

export const DocumentTemplatePage = ({
  isInPrintMode = false,
}: Readonly<Props>) => {
  const { setPage } = useNavigationContext();

  const [isLoading, setIsLoading] = useState(false);

  const documentTemplateId = useIdParam();

  const { data, isSuccess } =
    useFindOneDocumentTemplateQuery(documentTemplateId);

  useEffect(() => {
    setPage(Page.DocumentAndSectionTemplates);
  }, [setPage]);

  const { paths: editorModePaths } = useBuildPaths(
    {
      Info: PATH_DOCUMENT_TEMPLATE_INFO,
      InfoEdit: PATH_DOCUMENT_TEMPLATE_INFO_EDIT,
      Editor: PATH_DOCUMENT_TEMPLATE_EDITOR,
      Printer: PATH_DOCUMENT_TEMPLATE_PRINTER,
      Summary: PATH_DOCUMENT_TEMPLATE_SUMMARY,
      Preview: PATH_DOCUMENT_TEMPLATE_PREVIEW,
      Generation: PATH_DOCUMENT_TEMPLATE_GENERATION,
      Versions: PATH_DOCUMENT_TEMPLATE_VERSIONS,
      Admin: PATH_DOCUMENT_TEMPLATE_ADMIN,
      AdminEdit: PATH_DOCUMENT_TEMPLATE_ADMIN_EDIT,
    },
    { id: documentTemplateId },
  );

  const { onSave } = useSaveHandler({
    documentTemplateId,
    editorModePaths,
    setIsLoading,
  });

  const { onSavePreview } = useSavePreviewHandler({
    documentTemplateId,
    setIsLoading,
  });

  const path = useBuildPath(PATH_DOCUMENT_TEMPLATE_DEFAULT, {
    id: documentTemplateId,
  });

  const { onPrint } = usePrintHandler({
    documentTemplateId,
    snackbarActionTarget: path,
  });

  const { onSavePrintSettings } = useSavePrintSettingsHandler({
    documentTemplateId,
  });

  const getters = useGetters();

  const beforeTitleContent = (
    <>
      <Helmet>
        <title>
          {buildDocumentTitle(
            'Fiche Modèle de Document',
            data?.findOneDocumentTemplate.code,
          )}
        </title>
      </Helmet>
      <Breadcrumbs
        paths={[
          {
            to: PATH_DOCUMENT_AND_SECTION_TEMPLATES_DEFAULT,
            label: 'modèles',
            key: 'templates',
          },
          {
            to: PATH_DOCUMENT_TEMPLATES,
            label: 'documents',
            key: 'documentTemplates',
          },
          {
            to: path,
            label: 'fiche',
            key: 'documentTemplate',
          },
        ]}
      />
    </>
  );

  const titleContent = <PageTitle title="Fiche Modèle de Document" />;

  if (isSuccess) {
    const documentTemplate = {
      ...data.findOneDocumentTemplate,
      contentUpdatedAt: data.findOneDocumentTemplate.document.contentUpdatedAt,
    } as DocumentTemplatePage_OneDocumentTemplate;

    return (
      <>
        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
        {!!isLoading && <Loading />}
        {!isInPrintMode && (
          <>
            {beforeTitleContent}
            <PageTitle
              code={documentTemplate.code}
              title={documentTemplate.label}
            />
          </>
        )}
        <DocumentEditor
          componentSettings={{
            InfoTabPanel: DocumentTemplateInfoTabPanel,
            welcomeMessage:
              'Votre modèle de document est vide pour le moment. Commencez le travail !',
          }}
          formSettings={{
            formVariant: FormVariant.Modification,
            initialEditable: documentTemplate,
            isLoading,
          }}
          getters={getters}
          handlers={{
            onSavePreview,
            onSave,
            onPrint,
            onSavePrintSettings,
          }}
          resolversSettings={{
            allowedReturnTypeIds: undefined,
            enableDirectGeneration: false,
            mustBeResolvableOnBack: false,
          }}
          routerSettings={{
            editorModePaths,
            isInPrintMode,
          }}
          saveSettings={{
            askMemo: {
              beforePrint: false,
              beforeSave: true,
            },
            backupMode: BackupMode.Manual,
          }}
        />
      </>
    );
  }

  return (
    <>
      <Loading />
      {beforeTitleContent}
      {titleContent}
    </>
  );
};
